import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Email from "@material-ui/icons/Email";
import { client } from "../../services/axiosClient";
import { useTranslation } from "react-i18next";

// core components
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import CustomInput from "../../components/CustomInput/CustomInput.js";
import Button from "../../components/CustomButtons/Button.js";
import Card from "../../components/Card/Card.js";
import CardBody from "../../components/Card/CardBody.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardFooter from "../../components/Card/CardFooter.js";
import LoaderBar from "../../components/Loaders/LoaderBar.js";
import styles from "../../assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";
import cx from "classnames";

const SEND_MAIL_ROUTE = "/api/password-reset/request/";

const useStyles = makeStyles(styles);

export default function PasswordRecovery() {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const email = formData.get("email");

    setIsSubmitting(true);
    setError("");

    try {
      await client.post(SEND_MAIL_ROUTE, { email });
      setEmailSent(true);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(t(`forgot_password.errors.${err.response.data.error}`));
      } else {
        setError(t("forgot_password.errors.ERROR_DEFAULT"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <div className={classes.container}>
        <GridContainer justifyContent="center">
          <GridItem xs={12} sm={8} md={4}>
            <Card>
              <CardHeader
                className={`${classes.cardHeader} ${classes.textCenter}`}
                color="integration"
              >
                <h4 className={classes.cardTitle}>
                  {t("forgot_password.email_sent_title")}
                </h4>
              </CardHeader>
              <CardBody>
                <p className={classes.textCenter}>
                  {t("forgot_password.email_sent_message")}
                </p>
                <div className={classes.textCenter}>
                  <Button
                    color="integration"
                    simple
                    size="lg"
                    href="/auth/login"
                  >
                    {t("forgot_password.return_to_login")}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <GridContainer justifyContent="center">
        <GridItem xs={12} sm={8} md={4}>
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader
                className={`${classes.cardHeader} ${classes.textCenter}`}
                color="integration"
              >
                <h4 className={classes.cardTitle}>
                  {t("forgot_password.title")}
                </h4>
              </CardHeader>
              <CardBody>
                <p className={classes.textCenter}>
                  {t("forgot_password.instructions")}
                </p>
                <CustomInput
                  labelText={t("forgot_password.email")}
                  id="email"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Email className={classes.inputAdornmentIcon} />
                      </InputAdornment>
                    ),
                    type: "email",
                    name: "email",
                    disabled: isSubmitting,
                    required: true,
                  }}
                  integration
                />
                {error && (
                  <div className={classes.loginErrorMessage}>{error}</div>
                )}
              </CardBody>
              <CardFooter className={classes.justifyContentCenter}>
                <GridContainer>
                  <GridItem xs={12}>
                    <Button
                      type="submit"
                      color="integration"
                      simple
                      size="lg"
                      block
                      disabled={isSubmitting}
                    >
                      {t("forgot_password.send_instructions")}
                    </Button>
                  </GridItem>
                  <GridItem xs={12}>
                    <div className={classes.forgotPassword}>
                      <Button
                        color="white"
                        simple
                        size={"sm"}
                        className={cx({
                          [classes.buttonDisabled]: isSubmitting,
                        })}
                        href="/auth/login"
                      >
                        {t("forgot_password.back_to_login")}
                      </Button>
                    </div>
                  </GridItem>
                </GridContainer>
              </CardFooter>
              {isSubmitting && <LoaderBar />}
            </Card>
          </form>
        </GridItem>
      </GridContainer>
    </div>
  );
}
